/* ============================================================
   Fleet join page — /join?invite=ID
   Handles parent AND driver onboarding via email link.

   Flow:
     1. Firebase email-link sign-in (link fired by admin from BusTracking)
     2. Set password
     3. Role-specific form:
        • Parent  → name, phone → pick bus → add children (up to 4)
        • Driver  → name, phone, DOB → create a bus (plate, seats, petrol/diesel) or join one
                    — the profile AND the bus are minted by the backend (2026-09-16):
                    POST /api/driver/self-signup with proof { inviteId }. firestore.rules
                    refuses a client-created driver profile, so this page no longer writes one.
     4. Done → app download nudge (day-to-day is mobile)

   Tester accounts (isPilot org): same flow, just flagged isTester=true.
   Forgot password: Firebase handles it automatically — nothing special needed.
   ============================================================ */
import React, { useEffect, useRef, useState } from 'react';
import { isSignInWithEmailLink, signInWithEmailLink, updatePassword } from 'firebase/auth';
import {
  arrayUnion, collection, doc, getDocs, getDoc, increment,
  limit, query, setDoc, serverTimestamp, updateDoc, where
} from 'firebase/firestore';
import {
  FaArrowLeft, FaArrowRight, FaBus, FaCheck,
  FaEye, FaEyeSlash, FaMobileAlt, FaPlus, FaSpinner, FaTimes
} from 'react-icons/fa';
import { auth, db } from '../firebase';
import { isValidPhone, phoneProblem } from '../utils/phoneRules';
import LocationPicker, { LatLng } from './LocationPicker';
import { sealLatLng, ensureLocationDek } from '../utils/locationCrypto';
import { isPaymentsConfigured, sendFleetInviteEmailApi, notifyCapacityReached } from '../services/paymentsApi';
import {
  buildDriverSelfSignupRequest, driverSignupProductFor, selfSignupDriver,
  DriverSelfSignupError, DriverSignupErrorField,
  claimParentMembership,
  fetchInviteBusPlates,
  linkCoParentMembership,
} from '../services/driversApi';
import { customToast } from '../utils/customToast';
import '../site.css';
import './SubscriptionUI.css';
import '../site-v3/legacy-v3.css';

/* ─── types ──────────────────────────────────────────────────── */
type JoinRole = 'parent' | 'bus_driver';
type Phase = 'checking' | 'needEmail' | 'setpw' | 'details' | 'bus' | 'children' | 'done' | 'error';

interface BusRow { id: string; name: string; plate: string; }
/** Per-child entry. Each child can ride a different bus (by plate); child 2+ default to
 *  the first child's bus via `sameAsFirst`. Mirrors the mobile parent final-signup. */
interface ChildRow { name: string; classLabel: string; busPlate: string; sameAsFirst: boolean; stopLocation: LatLng | null; }

interface Invite {
  email: string;
  orgCode: string;
  orgName: string;
  role: JoinRole;
  isPilot?: boolean;
  isTester?: boolean;
  /** Stamped when a Families parent invites another adult in the household. */
  familyCoParent?: boolean;
  primaryParentId?: string;
  inheritedBusId?: string;
  inheritedChildren?: unknown[];
  inheritedChildAvatars?: unknown[];
  inheritedLinkedChildren?: unknown[];
  inheritedBusChildren?: unknown;
}

/* ─── helpers ─────────────────────────────────────────────────── */
function randomId() { return Math.random().toString(36).slice(2, 10); }

/** Kenyan plate → canonical doc-id form (KAA 123 A → KAA123A), matching the mobile app. */
function normalizePlate(s: string): string { return (s || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }

/** Buses are keyed by global plate, so an existing bus may belong to ANOTHER org. Never
 *  write to / join a bus that isn't this org's — that would leak across tenants. */
function busBelongsToOrg(data: Record<string, unknown> | undefined, orgCode: string): boolean {
  return !!data && (
    data.schoolCode === orgCode ||
    data.organizationCode === orgCode ||
    data.orgCode === orgCode
  );
}

function isFamilyOrgCode(code: string | undefined): boolean {
  return (code || '').toLowerCase().endsWith('.family');
}

/** Fallback when a Families invite was sent before we stamped the roster on it. */
async function firstHouseholdBus(orgCode: string): Promise<{ id: string; children: unknown[] } | null> {
  for (const field of ['schoolCode', 'organizationCode', 'orgCode'] as const) {
    const snap = await getDocs(query(collection(db, 'buses'), where(field, '==', orgCode), limit(1)));
    const d = snap.docs[0];
    if (!d) continue;
    const children = Array.isArray(d.data().children) ? d.data().children : [];
    return { id: d.id, children };
  }
  return null;
}

/** The org's product decides which phone sign-up screen a driver "is" on for the backend
 *  (driverSignupProductFor). Null when the org cannot be read — the code's suffix then decides. */
async function orgProductType(orgCode: string): Promise<string | null> {
  try {
    const snap = await getDoc(doc(db, 'organizations', orgCode));
    const pt = snap.exists() ? snap.data()?.productType : null;
    return typeof pt === 'string' ? pt : null;
  } catch {
    return null;
  }
}

/** Kenyan mobile: 07XXXXXXXX, or +254/254 7XXXXXXXX. Last-9 matching is not a format check. */
/* 2026-09-30: the shared per-country rules (utils/phoneRules) — length,
   first digit, and "numbers nobody has" — instead of a bare 07 regex. */
function isValidKenyanPhone(raw: string): boolean {
  return isValidPhone(raw, '+254');
}

/** Whole years old on local today. Negative if the date is in the future. */
function ageOnDate(iso: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const born = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(born.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (born > today) return -1;
  let age = today.getFullYear() - born.getFullYear();
  const md = today.getMonth() - born.getMonth();
  if (md < 0 || (md === 0 && today.getDate() < born.getDate())) age -= 1;
  return age;
}

function isoYearsAgo(n: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - n);
  return d.toISOString().slice(0, 10);
}

/** Match mobile signup: bus orgs get BUS_ONLY so the app does not send parents to classroom recovery. */
function productTypeFromOrgCode(orgCode: string): string {
  const c = (orgCode || '').toLowerCase();
  if (c.endsWith('.family')) return 'FAMILIES';
  if (c.endsWith('.bus')) return 'BUS_ONLY';
  if (c.endsWith('.lorry')) return 'LORRY';
  if (c.endsWith('.cargo')) return 'CARGO';
  if (c.endsWith('.fleet')) return 'SELF_TRACKING';
  return 'BUS_ONLY';
}

/* Page shell. MUST be module-scope: if declared inside the component it gets a new
   identity every render, React remounts the whole form on each keystroke, and focus
   jumps back to the autoFocus field (the "confirm password kicks to password" bug). */
const Shell = ({ children: c, step, total }: { children: React.ReactNode; step?: number; total?: number }) => (
  <div className="site-theme v3" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#F6F5F1' }}>
    <header style={{ padding: '13px 18px', background: '#F6F5F1', borderBottom: '1px solid #E4E3DE', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
      {/* The trasse mark (2026-10-28) — this page used to say PEAR. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, color: '#0F1419' }}>
        <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
          <rect width="28" height="28" rx="7" fill="#0F1419" />
          <path d="M10 7.5v10.2c0 2.2 1.2 3.3 3.3 3.3H17M7.5 11.5H17" stroke="#F6F5F1" strokeWidth="2.6" fill="none" strokeLinecap="round" />
          <circle cx="20.5" cy="21" r="2.2" fill="#0FB8CE" />
        </svg>
        <b style={{ fontFamily: "'Barlow Semi Condensed', sans-serif", fontWeight: 700, fontSize: 22 }}>trasse</b>
      </div>
      {step && total && (
        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#8C9196', fontWeight: 600 }}>Step {step} of {total}</div>
      )}
    </header>
    <main style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 14px 32px' }}>{c}</main>
  </div>
);

export default function FleetJoinPage() {
  /* ── URL params ── */
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const inviteId = params.get('invite') || '';

  /* ── invite meta (loaded from fleetInvites) ── */
  const [invite, setInvite] = useState<Invite | null>(null);
  const [phase, setPhase] = useState<Phase>('checking');
  const [err, setErr] = useState('');
  /** The input the backend named in a driver sign-up refusal — outlined and focused. */
  const [errField, setErrField] = useState<DriverSignupErrorField | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const plateRef = useRef<HTMLInputElement>(null);

  /* ── step-specific state ── */
  const [emailInput, setEmailInput] = useState('');
  const [confirmedEmail, setConfirmedEmail] = useState('');
  const [password, setPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);

  // details step
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [vehicleType, setVehicleType] = useState<'minibus' | 'large_bus' | ''>('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  // parent co-parent flow
  const [isCoParent, setIsCoParent] = useState(false);
  const [coParentCode, setCoParentCode] = useState('');

  // bus step
  const [buses, setBuses] = useState<BusRow[]>([]);
  const [selectedBusId, setSelectedBusId] = useState('');
  const [busLoading, setBusLoading] = useState(false);
  const [busPlateSearch, setBusPlateSearch] = useState('');
  // driver bus setup (full mobile parity: create a new bus or join an existing one by plate)
  const [isJoiningBus, setIsJoiningBus] = useState(false);
  const [driverBusPlate, setDriverBusPlate] = useState('');
  const [seatCapacity, setSeatCapacity] = useState('');
  const [fuelType, setFuelType] = useState<'diesel' | 'petrol'>('diesel');

  // children step (parents only)
  const [children, setChildren] = useState<ChildRow[]>([{ name: '', classLabel: '', busPlate: '', sameAsFirst: true, stopLocation: null }]);
  // Co-parent optional home — extra safe drop-off only; primary stopLocation stays as-is.
  const [coParentHome, setCoParentHome] = useState<LatLng | null>(null);

  const [saving, setSaving] = useState(false);
  const ranRef = useRef(false);

  /* ── 1. Verify the email link on mount ── */
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      setPhase('error');
      setErr('This join link is invalid or has expired. Ask your fleet admin to send a new one.');
      return;
    }
    // Try to get email from localStorage (set when admin fired the invite link)
    const stored = localStorage.getItem(`pear_invite_email_${inviteId}`);
    if (stored) {
      void completeSignIn(stored);
    } else {
      setPhase('needEmail');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function completeSignIn(email: string) {
    setErr('');
    setPhase('checking');
    try {
      await signInWithEmailLink(auth, email, window.location.href);
      const user = auth.currentUser!;
      await ensureLocationDek();
      setConfirmedEmail(email.trim().toLowerCase());

      // Load invite doc
      let inviteData: Invite | null = null;
      if (inviteId) {
        const snap = await getDoc(doc(db, 'fleetInvites', inviteId));
        if (snap.exists()) { inviteData = snap.data() as Invite; setInvite(inviteData); }
      }

      /* 2026-09-17: a parent's membership of the school, recorded by the server from
         the invite, BEFORE the page reads anything about the school's buses — bus
         reads are limited to a vehicle's own people (firestore.rules busReadOk), and
         the children step checks plates against them. A refused invite stops here;
         a network failure carries on (the claim is made again on submit). */
      if (inviteData?.role === 'parent' && inviteId) {
        const claimed = await claimParentMembership(inviteId);
        if (!claimed.ok && !/Could not confirm your invite/.test(claimed.error)) {
          setErr(claimed.error);
          setPhase('error');
          return;
        }
      }

      // If user already fully completed signup (e.g. clicking a second invite link),
      // skip straight to done so they're not forced to re-enter everything.
      const existingDoc = await getDoc(doc(db, 'users', user.uid));
      if (existingDoc.exists() && existingDoc.data()?.signupComplete === true) {
        setPhase('done');
        return;
      }

      setPhase('setpw');
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code;
      if (code === 'auth/invalid-email') {
        setErr("That email doesn't match this link. Enter the email your admin invited.");
        setPhase('needEmail');
      } else if (code === 'auth/invalid-action-code') {
        setErr('This link has already been used or expired. Ask your admin to send you a new invite.');
        setPhase('error');
      } else {
        setErr((e as Error)?.message || 'Could not verify your link.');
        setPhase('error');
      }
    }
  }

  async function handleSetPassword(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    if (password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (password !== confirmPw) { setErr('Passwords do not match.'); return; }
    const user = auth.currentUser;
    if (!user) { setErr('Session expired. Reopen the link from your email.'); setPhase('error'); return; }
    try {
      await updatePassword(user, password);
      setPhase('details');
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code;
      if (code === 'auth/requires-recent-login') {
        // Session too old — this happens if the user waited too long or the link
        // was opened in a different browser. Admin just needs to resend the invite.
        setErr('Your session has expired. Ask your fleet admin to send you a new invite link.');
        setPhase('error');
      } else {
        setErr((e as Error)?.message || 'Could not set password.');
      }
    }
  }

  // Send the person back to the input the server refused (2026-09-16). name and phone
  // live on the details step, the plate on the bus step; the catch in handleComplete
  // switches the step, and this focuses once that step is on screen.
  useEffect(() => {
    if (!errField) return;
    const ref = errField === 'plate' ? plateRef : errField === 'phone' ? phoneRef : errField === 'name' ? nameRef : null;
    ref?.current?.focus();
  }, [errField, phase]);

  // Load buses as soon as we know the org code (invite loads during completeSignIn).
  // This way buses are ready before the user reaches the bus-selection step,
  // and we can skip that step entirely when the org has no buses yet.
  useEffect(() => {
    const orgCode = invite?.orgCode;
    if (!orgCode) return;
    if (!inviteId) return;
    setBusLoading(true);
    /* Through the server (2026-09-17): an invitee cannot read the school's buses
       directly (busReadOk). It checks the invite and returns plates and names only,
       already without hired, expired or inactive vehicles. */
    fetchInviteBusPlates(inviteId)
      .then(list => setBuses(list))
      .finally(() => setBusLoading(false));
  }, [invite?.orgCode, inviteId]);

  async function handleDetailsNext(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    const familyHousehold = isFamilyOrgCode(invite?.orgCode);
    const familyParent = isParent && familyHousehold;
    if (!displayName.trim()) { setErr('Please enter your name.'); return; }
    if (familyParent) {
      // Optional here — but if a number is given it has to be a real one
      // (utils/phoneRules, 2026-09-30). The Families product is Kenyan.
      if (phone.trim()) {
        const problem = phoneProblem(phone, '+254');
        if (problem) {
          setErr(`${problem} Or leave it blank.`);
          return;
        }
      }
    } else if (!isValidKenyanPhone(phone)) {
      setErr(phoneProblem(phone, '+254') || 'Enter a Kenyan mobile number (07XX XXX XXX).');
      return;
    }
    if (!isParent) {
      if (!dob) { setErr('Enter your date of birth.'); return; }
      const age = ageOnDate(dob);
      if (age == null || age < 18) {
        setErr('You must be 18 or older to create a driver account.');
        return;
      }
      if (age > 80) { setErr('Check the date of birth.'); return; }
    }
    if (!isParent && !vehicleType) { setErr('Please select your vehicle type.'); return; }
    // The app generates CHILDNAME-NNNN (child_service.dart generateInviteCode), so this is
    // NOT a 6-digit PIN — it is letters, a hyphen and 4 digits, and its length varies with
    // the child's name. Mobile's validator accepts anything from 5 characters; match it.
    if (isParent && isCoParent && !familyParent && coParentCode.trim().length < 5) {
      setErr("Enter the co-parent code from the primary parent's app — the child's name, a dash, then 4 digits.");
      return;
    }
    if (!termsAccepted) { setErr('Please accept the Terms of Service to continue.'); return; }

    // Check the phone number and the co-parent code HERE, not at Finish. Both are typed on
    // THIS screen, and both were only tested at the very end — so you filled in every child,
    // pressed Finish, and were sent back for something you entered several screens ago. A
    // refusal belongs where the person can still act on it.
    const user = auth.currentUser;
    if (user && invite) {
      setSaving(true);
      try {
        if (phone.trim()) {
          const phoneClash = await describePhoneConflict(user.uid, phone, invite.orgCode);
          if (phoneClash) { setErr(phoneClash); return; }
        }

        if (isParent && isCoParent && !familyParent) {
          const { error } = await resolveCoParentCode(coParentCode, invite.orgCode);
          if (error) { setErr(error); return; }
        }
      } catch {
        setErr('Could not check those details just now. Check your connection and try again.');
        return;
      } finally {
        setSaving(false);
      }
    }

    // A Families invite is joining an existing household — name + password is enough.
    if (familyParent) {
      await handleComplete();
      return;
    }

    // Parent enters each child's bus (by plate) in the children step, so go straight there.
    // Driver goes to the bus step to create a new bus or join one by plate.
    setPhase(isParent ? 'children' : 'bus');
  }

  async function countStudentsOnOrgBuses(orgCode: string): Promise<number> {
    const seen = new Set<string>();
    let count = 0;
    for (const field of ['schoolCode', 'organizationCode'] as const) {
      const snap = await getDocs(query(collection(db, 'buses'), where(field, '==', orgCode)));
      for (const d of snap.docs) {
        const children = (d.data().children as Record<string, unknown>[] | undefined) || [];
        for (const c of children) {
          const id = String((c as { childId?: string; childName?: string }).childId
            || (c as { childName?: string }).childName
            || '').trim();
          if (!id || seen.has(id)) continue;
          seen.add(id);
          count += 1;
        }
      }
    }
    return count;
  }

  async function assertCanAddStudents(orgCode: string, additional: number): Promise<string | null> {
    if (additional <= 0) return null;
    const orgRef = doc(db, 'organizations', orgCode);
    const orgSnap = await getDoc(orgRef);
    if (!orgSnap.exists()) return null;
    const maxRaw = orgSnap.data()?.maxStudents;
    const max = typeof maxRaw === 'number' ? maxRaw : parseInt(String(maxRaw ?? ''), 10);
    if (!Number.isFinite(max) || max <= 0 || max >= 999999) return null;
    const current = await countStudentsOnOrgBuses(orgCode);
    if (current + additional > max) {
      /* The mobile app asks before telling the admin (see ZanaCapacityPrompt);
         here it still sends automatically, deliberately: this is a one-shot
         web form with no Zana surface to ask through, and the 24h cooldown on
         the backend now actually works — the composite index it needs was
         missing, which is what made this notify on every attempt. What was
         wrong either way was not saying so, leaving someone to chase an admin
         who had already been told. */
      void notifyCapacityReached({
        orgCode,
        maxStudents: max,
        currentStudents: current,
        source: 'fleet_join',
      });
      return `This school has reached its student limit (${max}). We've let the school admin know — ask them to raise the cap or free a seat, then try again.`;
    }
    return null;
  }

  async function bumpOrgUsage(orgCode: string, parents: number, students: number) {
    try {
      const orgSnap = await getDoc(doc(db, 'organizations', orgCode));
      if (!orgSnap.exists()) return;
      const updates: { [key: string]: ReturnType<typeof increment> | ReturnType<typeof serverTimestamp> } = {
        updatedAt: serverTimestamp(),
      };
      if (parents) updates.parentCount = increment(parents);
      if (students) updates.studentCount = increment(students);
      await updateDoc(orgSnap.ref, updates);
    } catch {
      /* best-effort */
    }
  }

  /**
   * Pre-flight the existing user doc.
   *
   * `firestore.rules` (selfUserUpdateOk) lets an account claim a role/org only while the
   * stored value is blank — absent or null. A mismatch is therefore a guaranteed
   * permission-denied a few lines below, and Firestore reports it as the useless
   * "Missing or insufficient permissions". Checking first turns it into something the
   * person reading the screen can actually act on. Returns null when the write will pass.
   */
  async function describeAccountConflict(uid: string, role: string, orgCode: string): Promise<string | null> {
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return null;
    const data = snap.data() as Record<string, unknown>;
    // null counts as blank — "Remove from fleet" nulls these rather than deleting them.
    const str = (k: string) => (typeof data[k] === 'string' ? (data[k] as string).trim() : '');
    const storedOrg = str('schoolCode') || str('organizationCode') || str('orgCode');
    const storedRole = str('role');

    if (storedOrg && storedOrg !== orgCode) {
      return `This email is already registered with another organization (${storedOrg}). `
        + `One email address can belong to one organization — ask your admin to send the invite to a different email.`;
    }
    if (storedRole && storedRole !== role) {
      const label = (r: string) => ({ parent: 'a parent', bus_driver: 'a driver',
        fleet_admin: 'an admin', admin: 'an admin', owner: 'an owner' } as Record<string, string>)[r] || `“${r}”`;
      return `This email is already registered as ${label(storedRole)} and cannot be reused as ${label(role)}. `
        + `Ask your admin to delete the old account first, or send the invite to a different email.`;
    }
    return null;
  }

  /**
   * Resolve a co-parent invite code, mirroring the app's `ChildService.validateInviteCode`.
   *
   * The code IS the document id in `inviteCodes`, uppercased — the app writes
   * `inviteCodes/{CODE}` with `{ childId, parentId, childName, childAvatar, expiry, used }`
   * (child_service.dart generateInviteCode). Returns either a resolved link or a message
   * the person can act on; never throws for an ordinary bad code.
   *
   * The app's second, legacy lookup path (scan every parent's `childInviteCodes`) is
   * deliberately not ported: it reads the whole users collection, and codes issued by any
   * current build live in `inviteCodes`.
   */
  async function resolveCoParentCode(rawCode: string, orgCode: string): Promise<{
    link?: {
      childId: string; primaryParentId: string; childName: string;
      childAvatar: string; codeId: string; primaryData: Record<string, unknown>;
    };
    error?: string;
  }> {
    const codeId = rawCode.trim().toUpperCase();
    if (!codeId) return { error: 'Enter the co-parent code from the primary parent.' };

    const snap = await getDoc(doc(db, 'inviteCodes', codeId));
    if (!snap.exists()) {
      return { error: `Code ${codeId} was not found. Check it in the primary parent's app under My Child › Add co-parent — it is the child's name, a dash, then 4 digits.` };
    }
    const data = snap.data() as Record<string, unknown>;

    if (data.used === true) {
      return { error: 'That code has already been used. Ask the primary parent to tap "Generate New Code".' };
    }
    const expiry = data.expiry as { toDate?: () => Date } | undefined;
    const expiresAt = typeof expiry?.toDate === 'function' ? expiry.toDate() : null;
    if (expiresAt && expiresAt.getTime() <= Date.now()) {
      return { error: 'That code has expired — codes last 24 hours. Ask the primary parent to tap "Generate New Code" and send you the new one.' };
    }

    const primaryParentId = typeof data.parentId === 'string' ? data.parentId : '';
    const childId = typeof data.childId === 'string' ? data.childId : '';
    const childName = typeof data.childName === 'string' ? data.childName : '';
    const childAvatar = typeof data.childAvatar === 'string' ? data.childAvatar : 'assets/images/cartoon1.png';
    if (!primaryParentId || !childId) {
      return { error: 'That code is incomplete. Ask the primary parent to generate a new one.' };
    }

    const primarySnap = await getDoc(doc(db, 'users', primaryParentId));
    if (!primarySnap.exists()) {
      return { error: 'The parent who created that code no longer has an account. Ask them to generate a new code.' };
    }
    const primaryData = primarySnap.data() as Record<string, unknown>;

    // Same-org guard, as the app does: a code from another school must not link across.
    const primaryOrg = [primaryData.organizationCode, primaryData.schoolCode, primaryData.orgCode]
      .find(v => typeof v === 'string' && (v as string).trim()) as string | undefined;
    if (primaryOrg && primaryOrg !== orgCode) {
      return { error: 'That code belongs to a different school. Use a code from a parent at this school.' };
    }

    return { link: { childId, primaryParentId, childName, childAvatar, codeId, primaryData } };
  }

  /**
   * Build the co-parent's view of the family from the primary's own document.
   *
   * `children` / `busChildren` / `childAvatars` are copied VERBATIM rather than reshaped:
   * the app and this page have historically written `children` as both a list of names and
   * a list of objects, and a copy is correct under either. `linkedChildren` is re-tagged
   * `role: 'coparent'`. Matches ChildService.linkCoParentBusOnly.
   */
  function coParentFamilyFields(link: {
    childId: string; primaryParentId: string; childName: string;
    childAvatar: string; primaryData: Record<string, unknown>;
  }): Record<string, unknown> {
    const p = link.primaryData;
    const children = Array.isArray(p.children) ? p.children : [];
    const avatars = Array.isArray(p.childAvatars) ? (p.childAvatars as unknown[]) : [];
    const primaryLinked = Array.isArray(p.linkedChildren) ? (p.linkedChildren as unknown[]) : [];

    let linkedChildren: Record<string, unknown>[] = primaryLinked
      .filter(c => c && typeof c === 'object')
      .map(c => ({ ...(c as Record<string, unknown>), role: 'coparent' }));

    if (!linkedChildren.length) {
      linkedChildren = (children as unknown[]).map((c, i) => {
        const obj = c && typeof c === 'object' ? (c as Record<string, unknown>) : null;
        const name = obj ? String(obj.childName ?? '') : String(c ?? '');
        return {
          childId: (obj?.childId as string) || `${link.primaryParentId}_child_${i}`,
          childName: name,
          childAvatar: (avatars[i] as string) || 'assets/images/cartoon1.png',
          ...(obj?.busId ? { busId: obj.busId } : {}),
          ...(obj?.className ? { className: obj.className, yearGroup: obj.className } : {}),
          role: 'coparent',
        };
      }).filter(c => String(c.childName).trim());
    }
    // The primary may have no roster yet (invite generated from a single child).
    if (!linkedChildren.length && link.childName) {
      linkedChildren = [{
        childId: link.childId, childName: link.childName,
        childAvatar: link.childAvatar, role: 'coparent',
      }];
    }

    return {
      children,
      busChildren: (p.busChildren && typeof p.busChildren === 'object') ? p.busChildren : {},
      childAvatars: avatars,
      linkedChildren,
      // `busChildren` above is a one-time snapshot and rules forbid the primary updating
      // this account later, so ChildService.refreshMyBusChildrenFromRoster re-derives it on
      // load. Recording the primary makes that match exact instead of falling back to the
      // child's name (bug 23).
      primaryParentId: link.primaryParentId,
    };
  }

  /**
   * Kenyan numbers arrive as 0712345678, 254712345678 or +254 712 345 678 and are the same
   * person. The last 9 digits are the identity — the same key `SchoolTripComposer` uses to
   * refuse duplicate conductor phones.
   */
  function phoneKeyOf(raw: string): string {
    const digits = (raw || '').replace(/\D/g, '');
    return digits.length >= 9 ? digits.slice(-9) : digits;
  }

  /**
   * Refuse a phone number already held by someone else. A phone is one person: shared
   * numbers break the trip-credential SMS (one handset receives two people's logins) and
   * make a parent impossible to identify on a bus roster.
   *
   * `phoneKey` is written on every account from now on, so this is exact going forward.
   * Accounts created before it exists are matched on the raw `phone` string as a fallback,
   * which only catches an identically-formatted duplicate — worth stating rather than
   * implying this is airtight for legacy rows.
   */
  async function describePhoneConflict(uid: string, rawPhone: string, orgCode: string): Promise<string | null> {
    const key = phoneKeyOf(rawPhone);
    if (key.length < 9) return null; // too short to be a real number; shape is validated elsewhere

    const seen = new Map<string, Record<string, unknown>>();
    const collect = async (field: string, value: string) => {
      try {
        const snap = await getDocs(query(collection(db, 'users'), where(field, '==', value), limit(20)));
        snap.docs.forEach(d => { if (d.id !== uid) seen.set(d.id, d.data() as Record<string, unknown>); });
      } catch {
        /* a failed lookup must not block signup — see the note above */
      }
    };
    await collect('phoneKey', key);
    if (rawPhone.trim()) await collect('phone', rawPhone.trim());

    for (const data of Array.from(seen.values())) {
      const theirOrg = [data.schoolCode, data.organizationCode, data.orgCode]
        .find(v => typeof v === 'string' && (v as string).trim()) as string | undefined;
      // Only refuse inside this organisation. Two schools may legitimately both hold a
      // number we cannot see the context for, and refusing across orgs would leak that an
      // account exists elsewhere.
      if (theirOrg !== orgCode) continue;
      if (data.suspended === true) continue; // a removed account should not block a re-join
      const who = typeof data.displayName === 'string' && data.displayName.trim()
        ? data.displayName.trim() : 'another account';
      return `The phone number ${rawPhone.trim()} is already used by ${who} at this school. `
        + `Each parent needs their own number — it is how drivers and trip messages reach the right person.`;
    }
    return null;
  }

  type ChildRecord = {
    childId: string; childName: string; className: string; yearGroup: string; busId: string;
    /** Plain {lat,lng} or sealed {v,iv,c}. */
    stopLocation: Record<string, unknown> | null;
    parentId: string; parentName: string; parentPhone: string;
    status: string; lastUpdated: Date;
  };

  /**
   * Build the records for children THIS account is adding itself, and pre-validate every
   * target bus before anything is written — a plate registered to another org must refuse,
   * and pre-checking avoids a partial write where the user doc saves but a bus write aborts.
   *
   * Shared by the parent and co-parent paths: a co-parent adds children only when they have
   * one the primary does not, but when they do it must behave identically.
   *
   * Returns an `error` string rather than throwing, so the caller can surface it and stop.
   */
  async function prepareChildRecords(uid: string, rows: ChildRow[], orgCode: string): Promise<{
    records: ChildRecord[];
    byBus: Map<string, ChildRecord[]>;
    busSnaps: Map<string, Awaited<ReturnType<typeof getDoc>>>;
    error?: string;
  }> {
    const empty = { records: [] as ChildRecord[], byBus: new Map<string, ChildRecord[]>(), busSnaps: new Map() };
    const firstPlate = rows[0]?.busPlate || '';
    const valid = rows.filter(c => c.name.trim());
    if (!valid.length) return empty;

    const capErr = await assertCanAddStudents(orgCode, valid.length);
    if (capErr) return { ...empty, error: capErr };

    const records: ChildRecord[] = valid.map((c, idx) => {
      const rawPlate = idx > 0 && c.sameAsFirst ? firstPlate : c.busPlate;
      const className = c.classLabel.trim();
      return {
        // MUST match the mobile parent signup format `${uid}_child_${index}` — the mobile
        // "primary parent" check keys on this prefix (random ids make them look like a
        // co-parent and break invite-code generation), and it's how children dedupe.
        childId: `${uid}_child_${idx}`,
        childName: c.name.trim(),
        className,
        yearGroup: className,
        busId: normalizePlate(rawPlate),
        // Estate-gate pin. Sealed for Firestore; legacy plain {lat,lng} still readable.
        stopLocation: null as Record<string, unknown> | null,
        parentId: uid,
        parentName: displayName.trim(),
        parentPhone: phone.trim(),
        status: 'not_picked',
        lastUpdated: new Date(), // serverTimestamp() is not allowed inside array elements
      };
    });

    // Seal pins after the sync map (AES-GCM is async in the browser).
    await Promise.all(records.map(async (r, idx) => {
      const src = valid[idx]?.stopLocation;
      if (!src) return;
      const sealed = await sealLatLng(src.lat, src.lng);
      r.stopLocation = sealed ?? {
        lat: src.lat,
        lng: src.lng,
      };
    }));

    const byBus = new Map<string, ChildRecord[]>();
    records.forEach(r => {
      if (!r.busId) return;
      byBus.set(r.busId, [...(byBus.get(r.busId) || []), r]);
    });

    const busSnaps = new Map<string, Awaited<ReturnType<typeof getDoc>>>();
    for (const busId of Array.from(byBus.keys())) {
      let snap;
      try {
        snap = await getDoc(doc(db, 'buses', busId));
      } catch (e) {
        // Another school's bus is refused by the rules (2026-09-17) — say which it is.
        if ((e as { code?: string })?.code === 'permission-denied') {
          return { ...empty, error: `Plate ${busId} is registered to another organization. Check the plate with your admin.` };
        }
        throw e;
      }
      if (snap.exists() && !busBelongsToOrg(snap.data(), orgCode)) {
        return { ...empty, error: `Plate ${busId} is registered to another organization. Check the plate with your admin.` };
      }
      // Hiding hired buses from the picker is not enough — the plate can be typed by
      // hand. A child put on a trip bus lands on a roster that expires with the trip,
      // taking their tracking with it.
      const b = snap.exists() ? (snap.data() as Record<string, unknown>) : null;
      if (b?.temporaryHire === true) {
        return { ...empty, error: `Plate ${busId} is a bus hired for a one-off trip, not one of the school's own buses. Ask your admin which bus your child rides every day.` };
      }
      if (b && (b.status === 'expired' || b.isActive === false)) {
        return { ...empty, error: `Bus ${busId} is no longer active at this school. Check the plate with your admin.` };
      }
      busSnaps.set(busId, snap);
    }
    return { records, byBus, busSnaps };
  }

  /** The `children` / `childAvatars` / `linkedChildren` shapes the mobile app expects. */
  function ownChildFields(records: ChildRecord[]) {
    return {
      children: records.map(c => ({
        childId: c.childId, childName: c.childName, busId: c.busId,
        className: c.className, yearGroup: c.yearGroup, stopLocation: c.stopLocation,
      })),
      childAvatars: records.map(() => 'assets/images/cartoon1.png'),
      linkedChildren: records.map(c => ({
        childId: c.childId, childName: c.childName, childAvatar: 'assets/images/cartoon1.png',
        yearGroup: c.yearGroup, className: c.className, busId: c.busId, stopLocation: c.stopLocation,
      })),
    };
  }

  /**
   * Stamp the co-parent's home onto every inherited child's bus roster row as a
   * `safeDropOffStops` entry. Does NOT change `stopLocation` — trips / Zana keep
   * using the primary pin; this only means drop-off at the co-parent's home is OK.
   */
  async function writeSafeDropOffsForFamily(
    coParentUid: string,
    orgCode: string,
    pin: LatLng,
    family: Record<string, unknown>,
    onStep: (s: string) => void,
  ) {
    const busIds = new Set<string>();

    const absorb = (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const m = raw as Record<string, unknown>;
      const busId = typeof m.busId === 'string' ? m.busId.trim() : '';
      if (busId) busIds.add(busId);
    };

    const linked = Array.isArray(family.linkedChildren) ? family.linkedChildren : [];
    linked.forEach(absorb);
    const kids = Array.isArray(family.children) ? family.children : [];
    kids.forEach(absorb);
    const busChildren = family.busChildren;
    if (busChildren && typeof busChildren === 'object') {
      Object.values(busChildren as Record<string, unknown>).forEach(v => {
        const id = typeof v === 'string' ? v.trim() : '';
        if (id) busIds.add(id);
      });
    }

    if (busIds.size === 0) {
      try {
        const snap = await getDocs(query(
          collection(db, 'buses'),
          where('schoolCode', '==', orgCode),
          limit(40),
        ));
        snap.docs.forEach(d => busIds.add(d.id));
      } catch {
        /* best-effort — account is already linked */
      }
    }

    const sealedPin = await sealLatLng(pin.lat, pin.lng);
    const entry = {
      parentId: coParentUid,
      ...(sealedPin ?? { lat: pin.lat, lng: pin.lng }),
    };

    for (const busId of Array.from(busIds)) {
      onStep(`saving your home on bus ${busId}`);
      const busRef = doc(db, 'buses', busId);
      const snap = await getDoc(busRef);
      if (!snap.exists()) continue;
      const data = snap.data() as Record<string, unknown>;
      const children = Array.isArray(data.children)
        ? (data.children as Record<string, unknown>[]).map(c => ({ ...c }))
        : [];
      let changed = false;
      for (let i = 0; i < children.length; i++) {
        const c = children[i];
        const rosterParent = typeof c.parentId === 'string' ? c.parentId.trim() : '';
        const primaryId = typeof family.primaryParentId === 'string'
          ? family.primaryParentId.trim() : '';
        // The linked family's rows only — the primary parent's children (2026-09-17).
        // This matched by child id or NAME too, so another family's child with the same
        // name got this co-parent's drop-off; the roster rules now refuse any write that
        // touches another parent's row, which would fail the whole join. A row this
        // co-parent owns as primary is never theirs to re-pin here either.
        if (rosterParent === '' || rosterParent !== primaryId) continue;

        const existing = Array.isArray(c.safeDropOffStops)
          ? (c.safeDropOffStops as Record<string, unknown>[]).filter(
            e => !(typeof e?.parentId === 'string' && e.parentId === coParentUid),
          )
          : [];
        children[i] = {
          ...c,
          safeDropOffStops: [...existing, entry],
          lastUpdated: new Date(),
        };
        changed = true;
      }
      if (changed) {
        await updateDoc(busRef, { children, updatedAt: serverTimestamp() });
      }
    }
  }

  /**
   * Write each bus's roster. MUST run AFTER the user doc exists: firestore.rules lets a
   * parent touch `children` only via isParent(), which reads the role off their user doc.
   * Creates the bus when a parent signs up before the driver, so the driver can join it later.
   */
  async function writeBusRosters(
    uid: string, orgCode: string,
    byBus: Map<string, ChildRecord[]>,
    busSnaps: Map<string, Awaited<ReturnType<typeof getDoc>>>,
    onStep: (s: string) => void,
  ) {
    for (const [busId, recs] of Array.from(byBus.entries())) {
      onStep(`adding your child to bus ${busId}`);
      const busRef = doc(db, 'buses', busId);
      if (busSnaps.get(busId)?.exists()) {
        await updateDoc(busRef, { children: arrayUnion(...recs), updatedAt: serverTimestamp() });
      } else {
        // ownerId required by firestore.rules bus create (ownerId == auth.uid).
        await setDoc(busRef, {
          ownerId: uid,
          numberPlate: busId,
          nickname: `Bus ${busId}`,
          schoolCode: orgCode,
          organizationCode: orgCode,
          driverIds: [],
          children: recs,
          currentRoute: { type: null, startedAt: null, finishedAt: null, status: null },
          isActive: true,
          status: 'active',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    }
  }

  async function handleComplete() {
    setErr('');
    const user = auth.currentUser;
    if (!user || !invite) { setErr('Session lost. Please start again.'); return; }
    const familyHousehold = isFamilyOrgCode(invite.orgCode);
    if (invite.role === 'parent' && !isCoParent && !familyHousehold) {
      if (!children[0]?.name.trim()) { setErr("Add at least one child's name."); return; }
      if (!children[0]?.busPlate.trim()) { setErr("Enter the bus number plate for your first child."); return; }
      // Every named child must have a stop location (estate-gate pin) before we can finish.
      if (children.some(c => c.name.trim() && !c.stopLocation)) {
        setErr("Set a stop location on the map for every child.");
        return;
      }
    } else if (invite.role === 'parent' && isCoParent && !familyHousehold) {
      // A co-parent inherits the primary's children, so adding one is OPTIONAL — they only
      // fill this in for a child who is NOT already in the system. The stop pin is optional
      // too: they normally share the primary's pin, and would only set one if they live
      // somewhere else. But a child they DO name still needs a bus, or nothing can carry it.
      if (children.some(c => c.name.trim() && !c.busPlate.trim())) {
        setErr("Enter the bus number plate for the child you added, or clear their name.");
        return;
      }
    }
    setSaving(true);
    // Names the write in flight, so a rules refusal can say WHICH one failed instead of
    // leaving the next person to reproduce it with the browser console open (bug 27).
    let step = 'checking your account';
    try {
      const conflict = await describeAccountConflict(user.uid, invite.role, invite.orgCode);
      if (conflict) { setErr(conflict); setSaving(false); return; }

      // Already checked on the details step, where it can still be corrected. Repeated here
      // as the last word: minutes pass while children are filled in, and the number could
      // have been claimed in between. Normally a no-op.
      if (phone.trim()) {
        step = 'checking your phone number';
        const phoneClash = await describePhoneConflict(user.uid, phone, invite.orgCode);
        if (phoneClash) { setErr(phoneClash); setSaving(false); return; }
      }

      if (invite.role === 'parent') {
        /* 2026-09-17: the server records this parent's membership of the school (or
           household) from the invite — the roster rules require it for every write
           below. Before the account or any roster is written, so a refused invite
           leaves nothing half-done. */
        if (!inviteId) {
          setErr('This link carries no invite, so your account cannot be set up from it. Ask your school to send a new one.');
          setSaving(false); return;
        }
        step = 'confirming your invite';
        const claimed = await claimParentMembership(inviteId);
        if (!claimed.ok) { setErr(claimed.error); setSaving(false); return; }
      }

      step = 'saving your account';
      const userDoc: Record<string, unknown> = {
        uid: user.uid,
        email: user.email,
        displayName: displayName.trim(),
        name: displayName.trim(),
        ...(phone.trim()
          ? { phone: phone.trim(), phoneKey: phoneKeyOf(phone) }
          : {}),
        role: invite.role,
        organizationCode: invite.orgCode,
        schoolCode: invite.orgCode,
        orgCode: invite.orgCode,
        productType: productTypeFromOrgCode(invite.orgCode),
        emailVerified: true,
        signupComplete: true,
        createdAt: serverTimestamp(),
        isTester: invite.isTester || false,
        ...(dob ? { dateOfBirth: dob } : {}),
        ...(vehicleType ? { vehicleType } : {}),
        ...(isParent && isCoParent ? { isCoParent: true, coParentCode: coParentCode.trim() } : {}),
      };

      if (invite.role === 'bus_driver') {
        // The backend mints the driver (2026-09-16): users/{uid}, the bus (create) or its
        // driverIds (join), and it marks this invite used — firestore.rules refuses a
        // client-created driver profile, so nothing here writes one. The plate pre-checks
        // that used to live here (exists / this org / free) are the server's now: it answers
        // with a sentence and the input to fix, both shown as they come.
        const plate = normalizePlate(driverBusPlate);
        if (!plate) { setErr('Enter your bus number plate.'); setErrField('plate'); setSaving(false); return; }
        if (!inviteId) {
          setErr('This link carries no invite, so your account cannot be set up from it. Ask your fleet admin to send a new one.');
          setSaving(false); return;
        }
        step = 'setting up your driver account';
        const product = driverSignupProductFor(invite.orgCode, await orgProductType(invite.orgCode));
        await selfSignupDriver(buildDriverSelfSignupRequest({
          orgCode: invite.orgCode,
          product,
          proof: { inviteId },
          name: displayName,
          phone,
          dateOfBirth: dob,
          plate: driverBusPlate,
          joining: isJoiningBus,
          seatCapacity,
          fuelType,
          vehicleType,
        }));
      } else if (familyHousehold && invite.role === 'parent') {
        // Invited adult in a household: inherit the car and children. Do not ask
        // them to re-add kids or type a CHILDNAME-NNNN pin — they already belong.
        step = 'joining the household';
        let inheritedKids = Array.isArray(invite.inheritedChildren) ? invite.inheritedChildren : [];
        const inheritedAvatars = Array.isArray(invite.inheritedChildAvatars) ? invite.inheritedChildAvatars : [];
        const inheritedLinked = Array.isArray(invite.inheritedLinkedChildren)
          ? invite.inheritedLinkedChildren
          : [];
        let busId = (invite.inheritedBusId || '').trim();
        if (!busId) {
          for (const k of inheritedKids) {
            const id = k && typeof k === 'object' ? String((k as { busId?: string }).busId || '').trim() : '';
            if (id) { busId = id; break; }
          }
        }
        if (!inheritedKids.length || !busId) {
          const bus = await firstHouseholdBus(invite.orgCode);
          if (bus) {
            if (!busId) busId = bus.id;
            if (!inheritedKids.length) inheritedKids = bus.children;
          }
        }
        Object.assign(userDoc, {
          role: 'parent',
          productType: 'FAMILIES',
          isCoParent: true,
          primaryParentId: invite.primaryParentId || '',
          children: inheritedKids,
          childAvatars: inheritedAvatars,
          linkedChildren: inheritedLinked,
          ...(invite.inheritedBusChildren && typeof invite.inheritedBusChildren === 'object'
            ? { busChildren: invite.inheritedBusChildren }
            : {}),
          ...(busId ? { busId } : {}),
        });
        await setDoc(doc(db, 'users', user.uid), userDoc);
        await bumpOrgUsage(invite.orgCode, 1, 0);
      } else if (isCoParent) {
        // Co-parent: joins the primary parent's existing children via their app code.
        //
        // This used to write `children: []` and keep the code as a string, and nothing
        // ever resolved it — a co-parent signed up successfully into an empty account with
        // no children and no bus. The code is now resolved and the family copied across,
        // the same way ChildService.linkCoParentBusOnly does on mobile.
        step = 'checking the co-parent code';
        const { link, error } = await resolveCoParentCode(coParentCode, invite.orgCode);
        if (error || !link) {
          setErr(error || 'That co-parent code could not be used.');
          setSaving(false);
          return;
        }
        // The server checks the code against the primary parent's own account and records
        // the link the roster rules need before this co-parent may touch the family's
        // children (2026-09-17). Before any write, so a refusal leaves nothing behind. It
        // also burns the code.
        step = 'linking you to the family';
        const linkedFamily = await linkCoParentMembership(coParentCode);
        if (!linkedFamily.ok) {
          setErr(linkedFamily.error);
          setSaving(false);
          return;
        }
        link.primaryParentId = linkedFamily.primaryParentId;

        // OPTIONAL: a child of their own that the primary does not already have. Most
        // co-parents add none and simply inherit the family.
        step = 'checking the children you added';
        const own = await prepareChildRecords(user.uid, children, invite.orgCode);
        if (own.error) { setErr(own.error); setSaving(false); return; }

        step = 'linking you to the family';
        const family = coParentFamilyFields(link);
        Object.assign(userDoc, family);
        if (own.records.length) {
          // Inherited first, then their own. Inherited entries carry the PRIMARY's uid in
          // their childId, so these cannot collide.
          const mine = ownChildFields(own.records);
          userDoc.children = [...(family.children as unknown[]), ...mine.children];
          userDoc.childAvatars = [...(family.childAvatars as unknown[]), ...mine.childAvatars];
          userDoc.linkedChildren = [
            ...(family.linkedChildren as unknown[]),
            ...mine.linkedChildren.map(c => ({ ...c, role: 'coparent' })),
          ];
        }
        // Optional co-parent home — mirror on the account; roster write is below.
        if (coParentHome) {
          const sealedHome = await sealLatLng(coParentHome.lat, coParentHome.lng);
          const pin = sealedHome ?? { lat: coParentHome.lat, lng: coParentHome.lng };
          userDoc.safeDropOffLocation = pin;
          const linked = Array.isArray(userDoc.linkedChildren)
            ? (userDoc.linkedChildren as Record<string, unknown>[])
            : [];
          userDoc.linkedChildren = linked.map(c => {
            const id = typeof c.childId === 'string' ? c.childId : '';
            // Own new kids keep stopLocation; inherited get the safe-drop mirror.
            if (id.startsWith(`${user.uid}_`)) return c;
            return { ...c, safeDropOffLocation: pin };
          });
        }
        await setDoc(doc(db, 'users', user.uid), userDoc);

        if (own.records.length) {
          await writeBusRosters(user.uid, invite.orgCode, own.byBus, own.busSnaps, s => { step = s; });
        }

        if (coParentHome) {
          step = 'saving your home as an extra drop-off';
          await writeSafeDropOffsForFamily(
            user.uid, invite.orgCode, coParentHome, family, s => { step = s; },
          );
        }

        // The code was burned by the server when it recorded the link (above); a client
        // may no longer write another parent's inviteCodes doc.

        await bumpOrgUsage(invite.orgCode, 1, own.records.length);
      } else {
        // Parent: each child can ride a DIFFERENT bus (by plate). Child 2+ default to
        // child 1's bus unless they uncheck "same bus".
        const prep = await prepareChildRecords(user.uid, children, invite.orgCode);
        if (prep.error) { setErr(prep.error); setSaving(false); return; }

        // user doc children — EXACTLY the shape the mobile parent signup writes (no
        // `busChildren` map: its childId keys were being rendered as phantom children).
        Object.assign(userDoc, ownChildFields(prep.records));
        await setDoc(doc(db, 'users', user.uid), userDoc);

        await writeBusRosters(user.uid, invite.orgCode, prep.byBus, prep.busSnaps, s => { step = s; });

        await bumpOrgUsage(invite.orgCode, 1, prep.records.length);
      }

      // Mark invite as used. Not for a driver: /api/driver/self-signup marked it, and a
      // second write here would only race the server's (2026-09-16).
      if (inviteId && invite.role !== 'bus_driver') {
        step = 'marking your invite as used';
        await updateDoc(doc(db, 'fleetInvites', inviteId), { used: true, usedAt: serverTimestamp(), usedBy: user.uid });
      }

      localStorage.removeItem(`pear_invite_email_${inviteId}`);
      customToast.show('Account set up — welcome to trasse!', 'success');
      setPhase('done');
    } catch (e: unknown) {
      const fbErr = e as { code?: string; message?: string };
      if (e instanceof DriverSelfSignupError) {
        // The server's sentence, verbatim; `field` sends them back to the input it named.
        console.warn(`[FleetJoin] driver self-signup refused (${e.code}) while ${step}`);
        setErr(e.message);
        setErrField(e.field ?? null);
        if (e.field === 'name' || e.field === 'phone') setPhase('details');
      } else if (fbErr?.code === 'permission-denied') {
        // Never surface Firestore's bare "Missing or insufficient permissions" — it names
        // neither the write nor the reason, which is what made bug 27 expensive to trace.
        console.error(`[FleetJoin] permission denied while ${step}`, e);
        setErr(`The server refused this change while ${step}. `
          + `This usually means the email is already tied to another organization or role. `
          + `Ask your admin to re-send the invite to a different email address.`);
      } else {
        console.error(`[FleetJoin] failed while ${step}`, e);
        setErr(fbErr?.message || `Could not finish ${step}. Please try again.`);
      }
    } finally {
      setSaving(false);
    }
  }

  /* ─── children helpers ─────────────────────────────────────── */
  function updateChild(i: number, field: keyof ChildRow, val: string | boolean | LatLng | null) {
    setChildren(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: val } : c));
  }
  function addChild() {
    if (children.length < 4) setChildren(prev => [...prev, { name: '', classLabel: '', busPlate: '', sameAsFirst: true, stopLocation: null }]);
  }
  function removeChild(i: number) {
    if (children.length > 1) setChildren(prev => prev.filter((_, idx) => idx !== i));
  }

  /* ─── app deep link (done screen) ──────────────────────────── */
  function handleOpenApp() {
    const deepLink = `pear://login?email=${encodeURIComponent(confirmedEmail)}`;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isIOS = /iPhone|iPad|iPod/.test(ua);
    const storeUrl = isIOS
      ? 'https://apps.apple.com/us/search?term=Trasse+School+Bus'
      : 'https://play.google.com/store/search?q=Trasse+School+Bus&c=apps';
    // Try app scheme first; fall back to store after 2.5 s if app not installed
    window.location.href = deepLink;
    const t = window.setTimeout(() => { window.location.href = storeUrl; }, 2500);
    document.addEventListener('visibilitychange', () => { if (document.hidden) clearTimeout(t); }, { once: true });
  }

  /* ─── shell ────────────────────────────────────────────────── */
  const isParent = invite?.role === 'parent';
  const familyHousehold = isFamilyOrgCode(invite?.orgCode);
  const orgLabel = invite?.orgName || invite?.orgCode || 'your organization';
  // Live, no-extra-reads check against the org buses already loaded for this org.
  const busExistsInOrg = (plate: string) => {
    const p = normalizePlate(plate);
    return !!p && buses.some(b => normalizePlate(b.id) === p || normalizePlate(b.plate) === p);
  };

  const cardStyle: React.CSSProperties = {
    width: '100%', maxWidth: 480, background: '#fff', borderRadius: 20,
    boxShadow: '0 18px 50px rgba(20,30,60,.10)', border: '1px solid #E4E3DE',
    padding: '28px 20px',
    boxSizing: 'border-box',
  };

  const totalSteps = familyHousehold && isParent ? 2 : 3; // family co-parent: setpw → details

  /* ─── phases ────────────────────────────────────────────────── */
  if (phase === 'checking') return (
    <Shell>
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <FaSpinner className="spin" style={{ fontSize: 28, color: '#00707F' }} />
        <p style={{ marginTop: 14, color: '#5D646B' }}>Verifying your link…</p>
      </div>
    </Shell>
  );

  if (phase === 'error') return (
    <Shell>
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: 32 }}>⚠️</div>
        <h2 style={{ margin: '12px 0 6px' }}>Link problem</h2>
        <p style={{ color: '#5D646B' }}>{err}</p>
      </div>
    </Shell>
  );

  if (phase === 'needEmail') return (
    <Shell step={1} total={isParent ? 4 : 3}>
      <div style={cardStyle}>
        <div style={{ fontSize: 28, marginBottom: 12 }}>✉️</div>
        <h2 style={{ margin: '0 0 6px' }}>Confirm your email</h2>
        <p style={{ color: '#5D646B', marginTop: 0 }}>Enter the email your fleet admin invited.</p>
        <form onSubmit={e => { e.preventDefault(); if (emailInput.trim()) void completeSignIn(emailInput.trim()); }}>
          <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
            placeholder="you@example.com" autoFocus
            style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 15, margin: '12px 0' }} />
          {err && <div style={{ color: '#B42318', fontSize: 13, marginBottom: 8 }}>{err}</div>}
          <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%' }} disabled={!emailInput.trim()}>Continue</button>
        </form>
      </div>
    </Shell>
  );

  if (phase === 'setpw') return (
    <Shell step={1} total={totalSteps}>
      <div style={cardStyle}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>🔐</div>
        <h2 style={{ margin: '0 0 4px' }}>Create your password</h2>
        <p style={{ color: '#5D646B', marginTop: 0 }}>
          {invite ? <>Setting up your account for <b>{orgLabel}</b>.</> : 'Set a password to continue.'}
        </p>
        <form onSubmit={handleSetPassword}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>Email</label>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: '#1E7A4C', background: '#E4F1E9', border: '1px solid #BFDCCB', borderRadius: 20, padding: '2px 8px' }}>
              <FaCheck size={9} /> Verified
            </span>
          </div>
          <input value={confirmedEmail} readOnly
            style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #E4E3DE', background: '#EFEDE7', color: '#5D646B', fontSize: 14, margin: '0 0 14px' }} />
          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>Password</label>
          <div style={{ position: 'relative', margin: '5px 0 12px' }}>
            <input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPw(e.target.value)}
              placeholder="At least 8 characters" autoFocus
              style={{ width: '100%', padding: '11px 42px 11px 14px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 14 }} />
            <button type="button" onClick={() => setShowPw(s => !s)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#8C9196' }}>
              {showPw ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>Confirm password</label>
          <input type={showPw ? 'text' : 'password'} value={confirmPw} onChange={e => setConfirmPw(e.target.value)}
            placeholder="Re-enter password"
            style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 14, margin: '5px 0 14px' }} />
          {err && <div style={{ color: '#B42318', fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%' }}>
            Set password <FaArrowRight />
          </button>
        </form>
      </div>
    </Shell>
  );

  if (phase === 'details') return (
    <Shell step={2} total={totalSteps}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 14, background: isParent ? '#CDEFF4' : '#0F1419', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
            {isParent ? '👤' : '🚌'}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>{familyHousehold && isParent ? 'Join this household' : 'Your details'}</h2>
            <p style={{ margin: 0, fontSize: 12.5, color: '#5D646B' }}>{familyHousehold && isParent ? 'Co-parent' : isParent ? 'Parent account' : 'Driver account'} · {orgLabel}</p>
          </div>
        </div>
        <form onSubmit={handleDetailsNext}>
          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>Full name</label>
          <input ref={nameRef} value={displayName} onChange={e => { setDisplayName(e.target.value); setErrField(null); }} placeholder="e.g. Grace Mwangi"
            style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${errField === 'name' ? '#B42318' : '#E4E3DE'}`, fontSize: 14, margin: '5px 0 14px', boxSizing: 'border-box' }} />

          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>{familyHousehold && isParent ? 'Phone number (optional)' : 'Phone number'}</label>
          <input ref={phoneRef} value={phone} onChange={e => { setPhone(e.target.value); setErrField(null); }} placeholder={familyHousehold && isParent ? 'Any number we can reach you on' : '+254 7XX XXX XXX'} type="tel" inputMode="tel"
            style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${errField === 'phone' ? '#B42318' : '#E4E3DE'}`, fontSize: 14, margin: '5px 0 14px', boxSizing: 'border-box' }} />

          {/* Parent-only: co-parent toggle — hidden for Families (they already are the invited adult). */}
          {isParent && !familyHousehold && (
            <div style={{ background: '#F6F5F1', borderRadius: 12, padding: '13px 14px', margin: '0 0 14px', border: '1px solid #E4E3DE' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setIsCoParent(v => !v)}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0F1419' }}>Joining as a co-parent?</div>
                  <div style={{ fontSize: 12, color: '#8C9196', marginTop: 2 }}>Use the code from the primary parent&rsquo;s app — My Child &rsaquo; Add co-parent</div>
                </div>
                {/* toggle pill */}
                <div style={{ width: 44, height: 24, borderRadius: 12, background: isCoParent ? '#00707F' : '#E4E3DE', position: 'relative', flexShrink: 0, transition: 'background .2s' }}>
                  <div style={{ position: 'absolute', top: 3, left: isCoParent ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
                </div>
              </div>
              {isCoParent && (
                <div style={{ marginTop: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 700, color: '#5D646B' }}>Co-parent code</label>
                  {/* The app's code is CHILDNAME-NNNN, e.g. CHAD-4387. The old mask stripped
                      every non-digit and capped at 6, so the real code could not be typed at
                      all — CHAD-4387 became 4387 and was then rejected as too short. */}
                  <input
                    value={coParentCode}
                    onChange={e => setCoParentCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 24))}
                    /* A template, NOT a realistic-looking code: a plausible example reads as
                       a pre-filled value, and a real child's name in the hint is worse still. */
                    placeholder="XXXX-0000"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    maxLength={24}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 19, letterSpacing: 3, textAlign: 'center', margin: '6px 0 0', boxSizing: 'border-box', fontFamily: 'monospace', textTransform: 'uppercase' }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Driver-only: DOB + vehicle type */}
          {!isParent && (<>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>Date of birth</label>
            <input value={dob} onChange={e => setDob(e.target.value)} type="date"
              min={isoYearsAgo(80)} max={isoYearsAgo(18)}
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 14, margin: '5px 0 6px', boxSizing: 'border-box', color: dob ? '#0F1419' : '#8C9196' }} />
            <div style={{ fontSize: 11.5, color: '#5D646B', margin: '0 0 14px' }}>Drivers must be 18 or older.</div>

            <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>Vehicle type</label>
            <div style={{ display: 'flex', gap: 10, margin: '6px 0 14px' }}>
              {([
                { key: 'minibus', emoji: '🚐', label: 'Minibus', sub: '14–25 seats' },
                { key: 'large_bus', emoji: '🚌', label: 'Large bus', sub: '26+ seats' },
              ] as const).map(v => (
                <button key={v.key} type="button" onClick={() => setVehicleType(v.key)}
                  style={{ flex: 1, padding: '12px 8px', borderRadius: 12, border: `2px solid ${vehicleType === v.key ? '#00707F' : '#E4E3DE'}`, background: vehicleType === v.key ? '#E2F5F8' : '#fff', cursor: 'pointer', textAlign: 'center' }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{v.emoji}</div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#0F1419' }}>{v.label}</div>
                  <div style={{ fontSize: 11, color: '#8C9196' }}>{v.sub}</div>
                </button>
              ))}
            </div>
          </>)}

          {/* Terms & Conditions */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '4px 0 16px', padding: '12px 14px', background: '#F6F5F1', borderRadius: 10, border: '1px solid #E4E3DE' }}>
            <input type="checkbox" id="join-terms" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
              style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', flexShrink: 0, accentColor: '#00707F' }} />
            <label htmlFor="join-terms" style={{ fontSize: 12.5, color: '#5D646B', lineHeight: 1.5, cursor: 'pointer' }}>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#00707F', textDecoration: 'none' }}>Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#00707F', textDecoration: 'none' }}>Privacy Policy</a>
            </label>
          </div>

          {err && <div style={{ color: '#B42318', fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <button className="btn btn-primary btn-lg" type="submit" style={{ width: '100%' }} disabled={saving}>
            {saving ? <><FaSpinner className="spin" style={{ marginRight: 6 }} />Setting up…</> : familyHousehold && isParent ? <>Join household <FaCheck /></> : <>Continue <FaArrowRight /></>}
          </button>
        </form>
      </div>
    </Shell>
  );

  if (phase === 'bus' && !isParent) {
    // DRIVER: full mobile parity — create a new bus, or join an existing one by plate.
    const plateValid = driverBusPlate.trim().length > 0;
    const capValid = isJoiningBus || (!!seatCapacity.trim() && parseInt(seatCapacity, 10) > 0);
    return (
      <Shell step={3} total={totalSteps}>
        <div style={cardStyle}>
          <button onClick={() => setPhase('details')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8C9196', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <FaArrowLeft /> Back
          </button>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🚌</div>
          <h2 style={{ margin: '0 0 4px' }}>Set up your bus</h2>
          <p style={{ color: '#5D646B', marginTop: 0, fontSize: 13.5 }}>This is what parents see when they search for your route at {orgLabel}.</p>

          {/* Are you joining a bus? */}
          <div style={{ background: '#F6F5F1', borderRadius: 12, padding: '13px 14px', margin: '16px 0 14px', border: '1px solid #E4E3DE' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => setIsJoiningBus(v => !v)}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0F1419' }}>Are you joining a bus?</div>
                <div style={{ fontSize: 12, color: '#8C9196', marginTop: 2 }}>{isJoiningBus ? 'Joining an existing bus' : 'Creating a new bus'}</div>
              </div>
              <div style={{ width: 44, height: 24, borderRadius: 12, background: isJoiningBus ? '#00707F' : '#E4E3DE', position: 'relative', flexShrink: 0, transition: 'background .2s' }}>
                <div style={{ position: 'absolute', top: 3, left: isJoiningBus ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
              </div>
            </div>
          </div>

          <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>Bus number plate</label>
          {(() => {
            const exists = busExistsInOrg(driverBusPlate);
            const ok = isJoiningBus ? exists : !exists;
            const showStatus = driverBusPlate.trim().length > 0;
            const msg = isJoiningBus
              ? (exists ? '✓ Bus found' : '✗ No bus with that plate — turn off "joining" to create it')
              : (exists ? '✗ Plate already exists — turn on "joining" to join it' : '✓ Plate available');
            return (
              <>
                <input ref={plateRef} value={driverBusPlate} onChange={e => { setDriverBusPlate(e.target.value); setErrField(null); }} placeholder="KAA 123 A" autoFocus
                  style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1.5px solid ${errField === 'plate' ? '#B42318' : showStatus ? (ok ? '#1E7A4C' : '#B42318') : '#E4E3DE'}`, fontSize: 15, margin: '5px 0 6px', boxSizing: 'border-box', textTransform: 'uppercase', letterSpacing: 1 }} />
                {showStatus && <div style={{ fontSize: 12, fontWeight: 600, color: ok ? '#1E7A4C' : '#B42318', margin: '0 0 12px' }}>{msg}</div>}
              </>
            );
          })()}

          {/* Buses already at this org — tap to join one */}
          {buses.length > 0 && (
            <div style={{ margin: '0 0 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#8C9196', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Buses at {orgLabel}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {buses.map(b => (
                  <button key={b.id} type="button" onClick={() => { setDriverBusPlate(b.plate || b.id); setIsJoiningBus(true); setErrField(null); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, border: '1.5px solid #E4E3DE', background: '#F6F5F1', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#5D646B' }}>
                    <FaBus size={11} style={{ color: '#8C9196' }} /> {b.plate || b.id}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!isJoiningBus && (<>
            <label style={{ fontSize: 12.5, fontWeight: 700, color: '#5D646B' }}>Seat capacity</label>
            <input value={seatCapacity} onChange={e => setSeatCapacity(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 26" type="number" inputMode="numeric"
              style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 15, margin: '5px 0 14px', boxSizing: 'border-box' }} />

            <div style={{ fontSize: 13, fontWeight: 600, color: '#0F1419', margin: '0 0 6px' }}>What does this bus burn?</div>
            <div style={{ display: 'flex', gap: 8, margin: '0 0 14px' }}>
              {(['diesel', 'petrol'] as const).map(ft => (
                <button key={ft} type="button" onClick={() => setFuelType(ft)}
                  style={{ flex: 1, padding: '12px 8px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
                    background: fuelType === ft ? '#0F1419' : '#EFEDE7', color: fuelType === ft ? '#fff' : '#0F1419' }}>
                  {ft[0].toUpperCase() + ft.slice(1)}
                </button>
              ))}
            </div>

            <div style={{ fontSize: 11.5, color: '#5D646B', margin: '0 0 14px' }}>Your fleet admin sets the tank size. Until they do, this bus stays on High Risk.</div>
          </>)}

          {err && <div style={{ color: '#B42318', fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <button className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 4 }}
            disabled={saving || !plateValid || !capValid}
            onClick={() => void handleComplete()}>
            {saving ? <><FaSpinner className="spin" style={{ marginRight: 6 }} />Setting up…</> : <>Finish setup <FaCheck /></>}
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === 'bus') {
    const filteredBuses = busPlateSearch.trim()
      ? buses.filter(b =>
          b.name.toLowerCase().includes(busPlateSearch.toLowerCase()) ||
          b.plate.toLowerCase().includes(busPlateSearch.toLowerCase()))
      : buses;
    return (
      <Shell step={3} total={totalSteps}>
        <div style={cardStyle}>
          <button onClick={() => setPhase('details')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8C9196', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
            <FaArrowLeft /> Back
          </button>
          <div style={{ fontSize: 28, marginBottom: 10 }}>🚌</div>
          <h2 style={{ margin: '0 0 4px' }}>Which bus does your child take?</h2>
          <p style={{ color: '#5D646B', marginTop: 0, fontSize: 13.5 }}>Select the bus from {orgLabel}.</p>

          {/* Parent: plate/nickname search */}
          {isParent && buses.length > 0 && (
            <div style={{ margin: '14px 0 2px' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#8C9196', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Bus plate or nickname</label>
              <input
                value={busPlateSearch}
                onChange={e => setBusPlateSearch(e.target.value)}
                placeholder="e.g. KAA 123 A or Route B"
                style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid #E4E3DE', fontSize: 14, margin: '5px 0', boxSizing: 'border-box' }}
              />
            </div>
          )}

          {busLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><FaSpinner className="spin" style={{ color: '#00707F' }} /></div>
          ) : buses.length === 0 ? (
            <div style={{ background: '#FBEFD9', border: '1px solid #F3D9A4', borderRadius: 12, padding: 16, color: '#9A5B00', fontSize: 13.5, textAlign: 'center', margin: '14px 0' }}>
              No buses have been added to this org yet. You can skip for now — your fleet admin will link you to a bus from the dashboard.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '10px 0' }}>
              {filteredBuses.length === 0 && busPlateSearch.trim() && (
                <div style={{ fontSize: 13, color: '#8C9196', textAlign: 'center', padding: '12px 0' }}>No buses match "{busPlateSearch}"</div>
              )}
              {filteredBuses.map(b => (
                <button key={b.id} onClick={() => setSelectedBusId(b.id)} type="button"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12, border: `2px solid ${selectedBusId === b.id ? '#00707F' : '#E4E3DE'}`, background: selectedBusId === b.id ? '#E2F5F8' : '#fff', cursor: 'pointer', textAlign: 'left' }}>
                  <FaBus style={{ color: selectedBusId === b.id ? '#00707F' : '#8C9196', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#0F1419' }}>{b.name}</div>
                    <div style={{ fontSize: 12, color: '#8C9196' }}>{b.plate}</div>
                  </div>
                  {selectedBusId === b.id && <FaCheck style={{ marginLeft: 'auto', color: '#00707F' }} />}
                </button>
              ))}
            </div>
          )}

          {err && <div style={{ color: '#B42318', fontSize: 13, marginBottom: 10 }}>{err}</div>}
          <button className="btn btn-primary btn-lg" style={{ width: '100%', marginTop: 4 }}
            disabled={(buses.length > 0 && !selectedBusId) || saving}
            onClick={() => {
              if (buses.length > 0 && !selectedBusId) { setErr('Select a bus first.'); return; }
              isParent ? setPhase('children') : void handleComplete();
            }}>
            {saving ? 'Saving…' : isParent ? <>Add children <FaArrowRight /></> : <>Finish setup <FaCheck /></>}
          </button>
        </div>
      </Shell>
    );
  }

  if (phase === 'children') return (
    <Shell step={3} total={totalSteps}>
      <div style={cardStyle}>
        <button onClick={() => setPhase('details')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8C9196', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <FaArrowLeft /> Back
        </button>
        <div style={{ fontSize: 28, marginBottom: 10 }}>👧</div>
        <h2 style={{ margin: '0 0 4px' }}>{isCoParent ? 'Any other children?' : 'Add your children'}</h2>
        <p style={{ color: '#5D646B', marginTop: 0, fontSize: 13.5 }}>
          {isCoParent
            ? 'You already share the children on the account you just joined — you do not need to add them again. Everything below is optional. Only fill it in if you have a child who is NOT on that account yet, or if you live somewhere else and the bus should also be allowed to drop them at your home.'
            : 'Add up to 4 children and the bus each one rides — they can be on different buses.'}
        </p>
        {buses.length > 0 && (
          <div style={{ fontSize: 12, color: '#8C9196', marginTop: 2 }}>
            Buses at {orgLabel}: <span style={{ color: '#5D646B', fontWeight: 600 }}>{buses.map(b => b.plate || b.id).join(', ')}</span>
          </div>
        )}

        {isCoParent && (
          <div style={{ background: '#F6F5F1', borderRadius: 12, padding: '14px 16px', border: '1px solid #E4E3DE', marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#5D646B', marginBottom: 10 }}>
              Your home (optional)
            </div>
            <LocationPicker
              label="Your home gate (optional)"
              value={coParentHome}
              onChange={setCoParentHome}
              heightPx={200}
            />
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, margin: '16px 0' }}>
          {children.map((c, i) => {
            const firstPlate = children[0]?.busPlate || '';
            const namePh = isCoParent ? "Child's name (optional)" : "Child's name *";
            const platePh = isCoParent
              ? 'Bus number plate (if adding this child)'
              : 'Bus number plate * (e.g. KAA 123 A)';
            return (
              <div key={i} style={{ background: '#F6F5F1', borderRadius: 12, padding: '14px 16px', position: 'relative', border: '1px solid #E4E3DE' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#5D646B', marginBottom: 8 }}>
                  {isCoParent ? `Extra child ${i + 1} (optional)` : `Child ${i + 1}`}
                </div>
                {children.length > 1 && (
                  <button onClick={() => removeChild(i)} type="button"
                    style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#8C9196' }}>
                    <FaTimes size={12} />
                  </button>
                )}
                <input value={c.name} onChange={e => updateChild(i, 'name', e.target.value)}
                  placeholder={namePh} autoFocus={i === 0 && !isCoParent}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E4E3DE', fontSize: 14, marginBottom: 8 }} />
                <input value={c.classLabel} onChange={e => updateChild(i, 'classLabel', e.target.value)}
                  placeholder="Class / year group (e.g. Year 6B)"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #E4E3DE', fontSize: 14, marginBottom: 8 }} />

                {/* Per-child bus: child 1 enters a plate; child 2+ can reuse it or pick a different one. */}
                {i === 0 ? (<>
                  <input value={c.busPlate} onChange={e => updateChild(i, 'busPlate', e.target.value)}
                    placeholder={platePh}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${c.busPlate.trim() ? (busExistsInOrg(c.busPlate) ? '#1E7A4C' : '#D6D3CC') : '#E4E3DE'}`, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }} />
                  {c.busPlate.trim() && (
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: busExistsInOrg(c.busPlate) ? '#1E7A4C' : '#8C9196', marginTop: 4 }}>
                      {busExistsInOrg(c.busPlate) ? '✓ Bus found at this school' : 'ⓘ New bus — we’ll set it up for the driver to join'}
                    </div>
                  )}
                </>) : (<>
                  <div onClick={() => updateChild(i, 'sameAsFirst', !c.sameAsFirst)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#5D646B', marginBottom: c.sameAsFirst ? 0 : 8 }}>
                    <span style={{ width: 18, height: 18, borderRadius: 5, border: `2px solid ${c.sameAsFirst ? '#00707F' : '#D6D3CC'}`, background: c.sameAsFirst ? '#00707F' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {c.sameAsFirst && <FaCheck size={9} color="#fff" />}
                    </span>
                    Same bus as Child 1{firstPlate ? ` (${normalizePlate(firstPlate)})` : ''}
                  </div>
                  {!c.sameAsFirst && (<>
                    <input value={c.busPlate} onChange={e => updateChild(i, 'busPlate', e.target.value)}
                      placeholder="Bus number plate (e.g. KAA 456 B)"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${c.busPlate.trim() ? (busExistsInOrg(c.busPlate) ? '#1E7A4C' : '#D6D3CC') : '#E4E3DE'}`, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 }} />
                    {c.busPlate.trim() && (
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: busExistsInOrg(c.busPlate) ? '#1E7A4C' : '#8C9196', marginTop: 4 }}>
                        {busExistsInOrg(c.busPlate) ? '✓ Bus found at this school' : 'ⓘ New bus — we’ll set it up'}
                      </div>
                    )}
                  </>)}
                </>)}

                {/* Primary parent: required estate-gate pin. Co-parent adding an extra
                    child: optional (their home for the family is the section above). */}
                {!isCoParent && (
                  <div style={{ marginTop: 12 }}>
                    <LocationPicker
                      label="Pick-up / drop-off stop *"
                      value={c.stopLocation}
                      onChange={(loc) => updateChild(i, 'stopLocation', loc)}
                      heightPx={200}
                    />
                    {!c.stopLocation && (
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: '#B42318', marginTop: 4 }}>
                        Drop a pin on this child’s stop to continue.
                      </div>
                    )}
                  </div>
                )}
                {isCoParent && c.name.trim() && (
                  <div style={{ marginTop: 12 }}>
                    <LocationPicker
                      label="This child’s stop (optional)"
                      value={c.stopLocation}
                      onChange={(loc) => updateChild(i, 'stopLocation', loc)}
                      heightPx={200}
                    />
                    <div style={{ fontSize: 11.5, color: '#8C9196', marginTop: 4 }}>
                      Only needed if this child is not already on the other parent’s account.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {children.length < 4 && (
          <button onClick={addChild} type="button"
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '2px dashed #E4E3DE', borderRadius: 10, padding: '10px 16px', cursor: 'pointer', color: '#8C9196', fontSize: 13, width: '100%', justifyContent: 'center', marginBottom: 14 }}>
            <FaPlus size={11} /> Add another child
          </button>
        )}

        {err && <div style={{ color: '#B42318', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        {/* A co-parent may finish with nothing filled in — their children came with the
            account they joined. A child they DO name still needs a bus, but the stop pin
            stays optional: they normally share the primary's, and would only set one if
            they live somewhere else. */}
        <button className="btn btn-primary btn-lg" style={{ width: '100%' }}
          disabled={saving || (isCoParent
            ? children.some(c => c.name.trim() && !c.busPlate.trim())
            : (!children[0]?.name.trim() || !children[0]?.busPlate.trim() || children.some(c => c.name.trim() && !c.stopLocation)))}
          onClick={() => void handleComplete()}>
          {saving ? <><FaSpinner className="spin" style={{ marginRight: 6 }} />Setting up…</> : <>Finish setup <FaCheck /></>}
        </button>
      </div>
    </Shell>
  );

  // done
  return (
    <Shell>
      <div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ width: 60, height: 60, borderRadius: '50%', background: '#E4F1E9', border: '2px solid #BFDCCB', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: 26 }}>✅</div>
        <h2 style={{ margin: '0 0 6px', fontSize: 20 }}>You're all set{invite?.isTester ? ' (tester)' : ''}!</h2>
        <p style={{ color: '#5D646B', fontSize: 13.5, margin: '0 0 6px' }}>
          {isParent
            ? <>Your parent account for <b>{orgLabel}</b> is ready.</>
            : <>Your driver account for <b>{orgLabel}</b> is ready.</>}
        </p>
        <p style={{ color: '#8C9196', fontSize: 12.5, margin: '0 0 22px' }}>
          Logged in as <b style={{ color: '#5D646B' }}>{confirmedEmail}</b>
        </p>

        {/* Primary: smart open — tries app first, falls back to store */}
        <button onClick={handleOpenApp}
          style={{ width: '100%', padding: '15px', background: 'linear-gradient(135deg, #00707F 0%, #00707F 100%)', borderRadius: 14, border: 'none', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
          <FaMobileAlt style={{ fontSize: 20 }} /> Open trasse App
        </button>
        <p style={{ fontSize: 11.5, color: '#8C9196', textAlign: 'center', margin: '0 0 16px', lineHeight: 1.5 }}>
          Opens the app if installed — or takes you to the store to download it
        </p>

        {/* Fallback: explicit store links */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <a href="https://apps.apple.com/us/search?term=Trasse+School+Bus" target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 8px', background: '#0F1419', borderRadius: 10, color: '#fff', textDecoration: 'none', fontSize: 12.5, fontWeight: 600 }}>
            🍎 App Store
          </a>
          <a href="https://play.google.com/store/search?q=Trasse+School+Bus&c=apps" target="_blank" rel="noopener noreferrer"
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '11px 8px', background: '#00707F', borderRadius: 10, color: '#fff', textDecoration: 'none', fontSize: 12.5, fontWeight: 600 }}>
            ▶ Google Play
          </a>
        </div>

        <div style={{ fontSize: 12, color: '#D6D3CC', lineHeight: 1.6 }}>
          Log in with your email and the password you just set.<br />
          Forgot it? Tap "Forgot password" on the app login screen.
        </div>
      </div>
    </Shell>
  );
}

/* ─── Invite sender util (used from BusTracking) ─────────────── */
export async function sendFleetInvite(
  email: string,
  orgCode: string,
  orgName: string,
  role: JoinRole,
  sentByUid: string,
  opts?: {
    isTester?: boolean;
    isPilot?: boolean;
    familyHousehold?: {
      primaryParentId: string;
      busId?: string;
      children?: unknown[];
      childAvatars?: unknown[];
      linkedChildren?: unknown[];
      busChildren?: unknown;
    };
  }
): Promise<{ ok: boolean; inviteId?: string; error?: string }> {
  const newInviteId = randomId() + randomId();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const continueUrl = `${origin}/join?invite=${newInviteId}`;

  const lower = email.trim().toLowerCase();
  try {
    const { setDoc, doc, serverTimestamp, getDocs: _getDocs, collection: _col, query: _q, where: _where, updateDoc: _upd } =
      await import('firebase/firestore');

    // 1) Send the email FIRST. If this fails (e.g. rate limit), we return an error and
    //    do NOT write an invite doc — otherwise we'd leave an orphan "Pending" with no
    //    email behind it (the cause of duplicate rows).
    if (!isPaymentsConfigured()) {
      return { ok: false, error: 'Email service not configured (REACT_APP_PAYMENTS_URL).' };
    }
    try {
      const branded = await sendFleetInviteEmailApi({
        orgCode,
        email: lower,
        role,
        orgName,
        inviteId: newInviteId,
        continueUrl,
      });
      if (!branded.ok) {
        const err = branded.error || '';
        if (err.includes('Too many')) return { ok: false, error: 'Too many invites sent right now — please wait a few minutes and try again.' };
        if (err.includes('valid email')) return { ok: false, error: `"${lower}" is not a valid email address.` };
        if (err.includes('another organisation') || err.includes('another organization')) {
          return { ok: false, error: err };
        }
        return { ok: false, error: err || 'Could not send the invite email. Please try again.' };
      }
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code || (e as Error)?.message || '';
      if (code.includes('too-many-requests')) return { ok: false, error: 'Too many invites sent right now — please wait a few minutes and try again.' };
      if (code.includes('invalid-email')) return { ok: false, error: `"${lower}" is not a valid email address.` };
      return { ok: false, error: (e as Error)?.message || 'Could not send the invite email. Please try again.' };
    }

    // 2) Supersede any previous unused invite for this email+org. Two equality filters
    //    (email + orgCode) still need no composite index, and the orgCode filter is what
    //    lets firestore.rules prove this query is org-scoped — the rule refuses a list a
    //    fleet admin cannot be shown to own. `used` stays a client-side filter.
    try {
      const staleSnap = await _getDocs(_q(
        _col(db, 'fleetInvites'),
        _where('email', '==', lower),
        _where('orgCode', '==', orgCode),
      ));
      await Promise.all(staleSnap.docs
        .filter(d => (d.data() as Record<string, unknown>).used !== true)
        .map(d => _upd(d.ref, { superseded: true, used: true })));
    } catch {
      // non-fatal — stale cleanup is best-effort
    }

    // 3) Write the fresh invite (email already sent successfully).
    await setDoc(doc(db, 'fleetInvites', newInviteId), {
      email: lower,
      orgCode,
      orgName,
      role,
      sentBy: sentByUid,
      createdAt: serverTimestamp(),
      used: false,
      isTester: opts?.isTester || false,
      isPilot: opts?.isPilot || false,
      ...(opts?.familyHousehold ? {
        familyCoParent: true,
        primaryParentId: opts.familyHousehold.primaryParentId,
        inheritedBusId: opts.familyHousehold.busId || '',
        inheritedChildren: opts.familyHousehold.children || [],
        inheritedChildAvatars: opts.familyHousehold.childAvatars || [],
        inheritedLinkedChildren: opts.familyHousehold.linkedChildren || [],
        inheritedBusChildren: opts.familyHousehold.busChildren || {},
      } : {}),
    });
    localStorage.setItem(`pear_invite_email_${newInviteId}`, lower);
    return { ok: true, inviteId: newInviteId };
  } catch (e: unknown) {
    return { ok: false, error: (e as Error)?.message || 'Could not send invite.' };
  }
}
